import { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Debrief() {
  const [rating, setRating] = useState(0);
  const [recommend, setRecommend] = useState("");
  const [form, setForm] = useState({ clarity: "", change: "", testimonial: "", name: "", email: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async () => {
    if (!rating) {
      setError("Please give your overall experience a rating — one tap.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/debrief`, {
        rating,
        clarity: form.clarity,
        change: form.change,
        recommend,
        testimonial: form.testimonial,
        consent,
        name: form.name,
        email: form.email,
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      setError("Something didn't send — please try once more.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone">
      <header className="border-b border-ink/15">
        <div className="mx-auto flex max-w-3xl items-center px-6 py-5">
          <a href="/" data-testid="debrief-brand-link" className="flex items-center gap-3">
            <img
              src="/assets/wmwe-logo-dark.png"
              alt="WMWE logo"
              className="h-9 w-9 rounded-full border border-ink/15 bg-cream object-contain p-[3px]"
            />
            <span className="font-sans text-[11px] tracking-[0.35em] text-ink">
              RECON 2026 <span className="text-ink/40">// THE DEBRIEF</span>
            </span>
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16 md:py-24">
        {done ? (
          <div className="border border-ink/25 bg-manila p-10 text-center md:p-14" data-testid="debrief-success">
            <div className="inline-block -rotate-6 border-[3px] border-wine p-1.5">
              <div className="border border-wine px-6 py-3">
                <p className="font-sans text-sm font-semibold tracking-[0.25em] text-wine">
                  DEBRIEF RECEIVED
                </p>
              </div>
            </div>
            <h1 className="mt-8 font-serif text-4xl font-medium tracking-tight text-ink md:text-5xl">
              Thank you for <span className="italic text-wine">your honesty.</span>
            </h1>
            <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-smoke">
              Every answer shapes what RECON becomes next. And if you left a testimony — another
              woman needed to hear it.
            </p>
            <a
              href="https://theconnectionguru.co"
              target="_blank"
              rel="noreferrer"
              data-testid="debrief-membership-link"
              className="group relative mt-10 inline-flex items-center overflow-hidden border border-wine bg-wine px-8 py-4 font-sans text-[10px] tracking-[0.35em] text-bone"
            >
              <span className="absolute inset-0 translate-y-full bg-navy transition-transform duration-500 ease-out group-hover:translate-y-0" />
              <span className="relative">DISCOVER THE WMWE MEMBERSHIP →</span>
            </a>
            <p className="mt-4 font-sans text-[9px] tracking-[0.25em] text-ink/45">
              THE TABLE CONTINUES ALL YEAR AT THE CONNECTION GURU
            </p>
          </div>
        ) : (
          <>
            <p className="font-sans text-[11px] uppercase tracking-[0.4em] text-wine">
              Post-Gathering // Five Honest Minutes
            </p>
            <h1 className="mt-6 font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl">
              The <span className="italic text-wine">Debrief.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-smoke">
              Thank you for gathering with us. What you share here is read personally and shapes
              what RECON becomes next year. Short answers are perfect.
            </p>

            <div className="mt-12 border border-ink/20 bg-manila p-8 md:p-10">
              <p className="font-sans text-[10px] tracking-[0.3em] text-ink/50">
                01 // YOUR OVERALL EXPERIENCE *
              </p>
              <div className="mt-4 flex gap-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    data-testid={`rating-${n}`}
                    className={`flex h-12 w-12 items-center justify-center border font-serif text-xl transition-colors duration-200 ${
                      rating === n
                        ? "border-wine bg-wine text-bone"
                        : "border-ink/30 text-ink hover:border-wine"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="mt-2 font-sans text-[9px] tracking-[0.25em] text-ink/40">
                1 = NOT FOR ME // 5 = TRANSFORMATIVE
              </p>

              <p className="mt-10 font-sans text-[10px] tracking-[0.3em] text-ink/50">
                02 // WHICH MOMENT GAVE YOU THE MOST CLARITY?
              </p>
              <textarea
                value={form.clarity}
                onChange={set("clarity")}
                rows={2}
                data-testid="debrief-clarity"
                className="mt-3 w-full border border-ink/30 bg-transparent px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-wine"
              />

              <p className="mt-8 font-sans text-[10px] tracking-[0.3em] text-ink/50">
                03 // ONE THING YOU WOULD CHANGE
              </p>
              <textarea
                value={form.change}
                onChange={set("change")}
                rows={2}
                data-testid="debrief-change"
                className="mt-3 w-full border border-ink/30 bg-transparent px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-wine"
              />

              <p className="mt-10 font-sans text-[10px] tracking-[0.3em] text-ink/50">
                04 // WOULD YOU TELL A WOMAN IN YOUR CIRCLE TO COME NEXT YEAR?
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                {["ABSOLUTELY", "MAYBE", "NOT YET"].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setRecommend(opt)}
                    data-testid={`recommend-${opt.toLowerCase().replace(" ", "-")}`}
                    className={`border px-6 py-3 font-sans text-[10px] tracking-[0.3em] transition-colors duration-200 ${
                      recommend === opt
                        ? "border-wine bg-wine text-bone"
                        : "border-ink/30 text-ink/70 hover:border-wine"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>

              <p className="mt-10 font-sans text-[10px] tracking-[0.3em] text-ink/50">
                05 // YOUR TESTIMONY — WHAT WOULD YOU TELL A WOMAN CONSIDERING RECON?
              </p>
              <textarea
                value={form.testimonial}
                onChange={set("testimonial")}
                rows={3}
                data-testid="debrief-testimonial"
                className="mt-3 w-full border border-ink/30 bg-transparent px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-wine"
              />
              <label className="mt-3 flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="debrief-consent"
                  className="h-4 w-4 accent-[#580B2B]"
                />
                <span className="text-xs text-smoke">
                  You may share my words with the WMWE community
                </span>
              </label>

              <div className="mt-10 grid gap-5 md:grid-cols-2">
                <div>
                  <label className="block font-sans text-[9px] tracking-[0.25em] text-ink/45">
                    NAME (OPTIONAL)
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    data-testid="debrief-name"
                    className="mt-2 w-full border border-ink/30 bg-transparent px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-wine"
                  />
                </div>
                <div>
                  <label className="block font-sans text-[9px] tracking-[0.25em] text-ink/45">
                    EMAIL (OPTIONAL)
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    data-testid="debrief-email"
                    className="mt-2 w-full border border-ink/30 bg-transparent px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-wine"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={submit}
                disabled={loading}
                data-testid="debrief-submit-button"
                className="group relative mt-10 inline-flex w-full items-center justify-center overflow-hidden border border-wine bg-wine px-10 py-5 font-sans text-xs tracking-[0.4em] text-bone disabled:opacity-60"
              >
                <span className="absolute inset-0 translate-y-full bg-navy transition-transform duration-500 ease-out group-hover:translate-y-0" />
                <span className="relative">{loading ? "SENDING..." : "SUBMIT DEBRIEF"}</span>
              </button>
              {error && (
                <p data-testid="debrief-error" className="mt-4 font-sans text-[10px] tracking-[0.2em] text-wine">
                  {error}
                </p>
              )}
            </div>

            <p className="mt-8 text-center font-sans text-[9px] tracking-[0.3em] text-ink/45">
              A WMWE SIGNATURE GATHERING // THE TABLE CONTINUES AT THECONNECTIONGURU.CO
            </p>
          </>
        )}
      </main>
    </div>
  );
}
